'use client';
import useProducts from '@hooks/product/useProducts';
import { useCBQueue } from '@hooks/useCBQueue';
import React, { useEffect } from 'react';
import { buildFilters, SCHEMA_FILTERS } from '@functions/category-grid/product-grid-functions';
import { ProductGridProps } from '@typings/productGrid';
import { logProduct } from '@utils/productWrapper';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import GridItem from './_GridItem';
import { useProductGridData } from './useProductGridData';
import CategoryGrid, { GridType } from '@components/shared/CategoryGrid';

interface LoadMoreProps {
  page: number;
}

const ProductGrid = ({
  filters,
  filterOptions,
  sortOptions,
  _data,
  products: _products,
  ...props
}: ProductGridProps) => {
  const { locale } = useCurrentUrl();

  const sort = React.useMemo(() => {
    if (sortOptions?.options?.[0]?.value) {
      const [field, direction] = sortOptions.options[0].value.split(',');
      return { field, direction };
    }
  }, [sortOptions]);

  const { data, setSize, isValidating, isLoading } = useProductGridData(
    {
      filters: buildFilters(filters, locale),
      _data,
      sort,
      options: {
        // Only request facets for the filter options defined
        facets: filterOptions
          ?.map((x) => SCHEMA_FILTERS[x._meta?.schema as keyof typeof SCHEMA_FILTERS])
          ?.filter((x) => x),
      },
    },
    !!_products?.length,
  );

  const { products } = useProducts(data?.hits);

  if (products.length < (data.hits?.length ?? 0)) {
    console.info(
      'These skus are not in this locale',
      data?.hits?.filter((x) => !x.shop?.id).map((x) => x.productData?.sku),
    );
  }

  useEffect(() => {
    if (isValidating) {
      return;
    }
    logProduct('PRODUCT_LIST_VIEWED', {
      product: products ?? [],
      locale,
      list_name: props._meta.name,
      list_id: props._meta.deliveryId,
    });
  }, [products, isValidating]);

  const getNextPageData = React.useCallback(
    async ({ page }: LoadMoreProps) => {
      const data = await setSize(page + 1);
      return { data: data?.[0] };
    },
    [setSize],
  );
  const loadData = useCBQueue(getNextPageData, !isLoading);

  return (
    <CategoryGrid
      items={products}
      nbPages={data.nbPages}
      page={data.page}
      loadData={loadData}
      type={GridType.Product}
      GridItemComponent={GridItem}
      {...props}
    />
  );
};

export default ProductGrid;
